import { zodCreateInputOmit, zodUpdateInputOmit } from "../../helpers";
import { SupernovaException } from "../../utils";
import { DesignElementBase, DesignElementBrandedPart, DesignElementGroupableRequiredPart, DesignElementOrigin, } from "./base";
import { BlurTokenData, BorderRadiusTokenData, BorderTokenData, BorderWidthTokenData, ColorTokenData, DimensionTokenData, DurationTokenData, FontFamilyTokenData, FontSizeTokenData, FontTokenData, FontWeightTokenData, GradientTokenData, LetterSpacingTokenData, LineHeightTokenData, OpacityTokenData, ParagraphSpacingTokenData, ProductCopyTokenData, ShadowTokenData, SizeTokenData, SpaceTokenData, StringTokenData, TextCaseTokenData, TextDecorationTokenData, TypographyTokenData, VisibilityTokenData, ZIndexTokenData, } from "./data";
import { areTokenTypesCompatible, castStringToDimensionValue, } from "./raw-element";
import { z } from "zod";
//
// Base
//
export const DesignTokenOriginPart = z.object({
    referenceOriginId: z.string().optional(),
    referenceOriginKey: z.string().optional(),
    referenceOriginName: z.string().optional(),
    referenceOriginRemote: z.boolean().optional(),
    referencePersistentId: z.string().optional(),
    referenceResolutionFailed: z.boolean().optional(),
    key: z.string().optional(),
    sortOrder: z.number().optional(),
});
export const DesignTokenOrigin = DesignElementOrigin.extend(DesignTokenOriginPart.shape);
const DesignTokenBase = DesignElementBase.extend(DesignElementGroupableRequiredPart.shape)
    .extend(DesignElementBrandedPart.shape)
    .extend({
    origin: DesignTokenOrigin.optional(),
    collectionPersistentId: z.string().optional(),
});
const CreateDesignTokenBase = DesignTokenBase.omit(zodCreateInputOmit());
const UpdateDesignTokenBase = DesignTokenBase.omit({
    ...zodUpdateInputOmit(),
    brandPersistentId: true,
    designSystemVersionId: true,
});
//
// Per type
//
const BlurTokenTypedData = z.object({
    type: z.literal("Blur"),
    data: BlurTokenData,
});
const ColorTokenTypedData = z.object({
    type: z.literal("Color"),
    data: ColorTokenData,
});
const GradientTokenTypedData = z.object({
    type: z.literal("Gradient"),
    data: GradientTokenData,
});
const OpacityTokenTypedData = z.object({
    type: z.literal("Opacity"),
    data: OpacityTokenData,
});
const ShadowTokenTypedData = z.object({
    type: z.literal("Shadow"),
    data: ShadowTokenData,
});
const TypographyTokenTypedData = z.object({
    type: z.literal("Typography"),
    data: TypographyTokenData,
});
const StringTokenTypedData = z.object({
    type: z.literal("String"),
    data: StringTokenData,
});
const DimensionTokenTypedData = z.object({
    type: z.literal("Dimension"),
    data: DimensionTokenData,
});
const FontSizeTokenTypedData = z.object({
    type: z.literal("FontSize"),
    data: FontSizeTokenData,
});
const FontFamilyTokenTypedData = z.object({
    type: z.literal("FontFamily"),
    data: FontFamilyTokenData,
});
const FontWeightTokenTypedData = z.object({
    type: z.literal("FontWeight"),
    data: FontWeightTokenData,
});
const LetterSpacingTokenTypedData = z.object({
    type: z.literal("LetterSpacing"),
    data: LetterSpacingTokenData,
});
const LineHeightTokenTypedData = z.object({
    type: z.literal("LineHeight"),
    data: LineHeightTokenData,
});
const ParagraphSpacingTokenTypedData = z.object({
    type: z.literal("ParagraphSpacing"),
    data: ParagraphSpacingTokenData,
});
const TextCaseTokenTypedData = z.object({
    type: z.literal("TextCase"),
    data: TextCaseTokenData,
});
const TextDecorationTokenTypedData = z.object({
    type: z.literal("TextDecoration"),
    data: TextDecorationTokenData,
});
const BorderRadiusTokenTypedData = z.object({
    type: z.literal("BorderRadius"),
    data: BorderRadiusTokenData,
});
const BorderWidthTokenTypedData = z.object({
    type: z.literal("BorderWidth"),
    data: BorderWidthTokenData,
});
const BorderTypedData = z.object({
    type: z.literal("Border"),
    data: BorderTokenData,
});
const ProductCopyTypedData = z.object({
    type: z.literal("ProductCopy"),
    data: ProductCopyTokenData,
});
const SizeTypedData = z.object({
    type: z.literal("Size"),
    data: SizeTokenData,
});
const SpaceTypedData = z.object({
    type: z.literal("Space"),
    data: SpaceTokenData,
});
const VisibilityTypedData = z.object({
    type: z.literal("Visibility"),
    data: VisibilityTokenData,
});
const ZIndexTypedData = z.object({
    type: z.literal("ZIndex"),
    data: ZIndexTokenData,
});
const DurationTypedData = z.object({
    type: z.literal("Duration"),
    data: DurationTokenData,
});
const FontTypedData = z.object({
    type: z.literal("Font"),
    data: FontTokenData,
});
//
// Global types
//
export const DesignTokenTypedData = z.discriminatedUnion("type", [
    BlurTokenTypedData,
    BorderRadiusTokenTypedData,
    BorderWidthTokenTypedData,
    BorderTypedData,
    ColorTokenTypedData,
    DimensionTokenTypedData,
    DurationTypedData,
    FontSizeTokenTypedData,
    FontFamilyTokenTypedData,
    FontWeightTokenTypedData,
    GradientTokenTypedData,
    LetterSpacingTokenTypedData,
    LineHeightTokenTypedData,
    OpacityTokenTypedData,
    ParagraphSpacingTokenTypedData,
    ProductCopyTypedData,
    ShadowTokenTypedData,
    SizeTypedData,
    SpaceTypedData,
    StringTokenTypedData,
    TextCaseTokenTypedData,
    TextDecorationTokenTypedData,
    TypographyTokenTypedData,
    VisibilityTypedData,
    ZIndexTypedData,
    FontTypedData,
]);
export const DesignToken = DesignTokenTypedData.and(DesignTokenBase);
export const CreateDesignToken = DesignTokenTypedData.and(CreateDesignTokenBase);
//
// Utilities
//
export function extractTokenTypedData(source) {
    return {
        type: source.type,
        data: source.data,
    };
}
export function convertTokenTypedData(source, type, isNonCompatibleTypeChangesEnabled) {
    if (!areTokenTypesCompatible(source.type, type, isNonCompatibleTypeChangesEnabled)) {
        throw SupernovaException.invalidOperation(`Cannot convert token from ${source.type} to ${type}`);
    }
    const data = source.data;
    if (isNonCompatibleTypeChangesEnabled) {
        data.value = castStringToDimensionValue(source.type, type, source.data.value);
    }
    return {
        type,
        data,
    };
}
export function isImportedDesignToken(designToken) {
    return !!designToken.origin;
}
export function isDesignTokenOfType(designToken, type) {
    return designToken.type === type;
}
export function designTokenTypeFilter(type) {
    return (designToken) => isDesignTokenOfType(designToken, type);
}
